import { DailyWageReport, WeeklyReport } from '@/clients/omb-api';
import { ReportsRepo } from '@/repos/ReportsRepo';
import clone from 'clone';
import { v4 as uuidv4 } from 'uuid';

type ReportType = 'weekly' | 'daily-wage';
type StoredReport = WeeklyReport | DailyWageReport;

interface ReportsStoreEntry {
    key: string;
    reports: StoredReport[];
}

export default class ReportsIndexedDBRepo implements ReportsRepo {

    private readonly DB_NAME = 'OMB_REPORTS_DB';
    private readonly DB_VERSION = 1;
    private readonly STORE_NAME = 'reports_by_user_and_type';
    private readonly MIGRATION_MARKER_PREFIX = 'OMB_REPORTS_INDEXEDDB_MIGRATED';

    private dbPromise?: Promise<IDBDatabase>;

    public async saveWeeklyReports(reports: WeeklyReport[]): Promise<WeeklyReport[]> {
        return this.saveReports(reports, 'weekly') as Promise<WeeklyReport[]>;
    }

    public async saveDailyWageReports(reports: DailyWageReport[]): Promise<DailyWageReport[]> {
        return this.saveReports(reports, 'daily-wage') as Promise<DailyWageReport[]>;
    }

    private async saveReports(reports: StoredReport[], reportType: ReportType): Promise<StoredReport[]> {
        const userId = this.getUserId();
        if (!userId) return [];

        await this.ensureMigrationDone(userId);

        let allReports = await this.getReports(reportType, userId);
        const savedReports: StoredReport[] = [];

        for (let i = 0; i < reports.length; i += 1) {
            const report = clone(reports[i]);

            report.offline = true;
            if (!report.offlineId) {
                report.offlineId = uuidv4();
            }

            const existing = allReports.find((r) => this.isSameReport(r, report));
            if (existing) {
                allReports = allReports.map((r) => (this.isSameReport(r, report) ? report : r));
            } else {
                allReports.push(report);
            }

            savedReports.push(report);
        }

        await this.setReports(reportType, userId, allReports);

        return savedReports;
    }

    public async findAllWeeklyReports(): Promise<WeeklyReport[]> {
        return await this.getAllReports('weekly') as WeeklyReport[];
    }

    public async findAllDailyWageReports(): Promise<DailyWageReport[]> {
        return await this.getAllReports('daily-wage') as DailyWageReport[];
    }

    public async findWeeklyReportById(reportId: number): Promise<WeeklyReport> {
        const allReports = await this.getAllReports('weekly') as WeeklyReport[];
        const report = allReports.find((r) => r.id === reportId);
        if (report) return report;
        throw new Error('Weekly report not found in IndexedDB');
    }

    public async findDailyWageReportById(reportId: number): Promise<DailyWageReport> {
        const allReports = await this.getAllReports('daily-wage') as DailyWageReport[];
        const report = allReports.find((r) => r.id === reportId);
        if (report) return report;
        throw new Error('Daily wage report not found in IndexedDB');
    }

    public async findAllWeeklyReportsByProjectGroup(projectGroupId: number): Promise<WeeklyReport[]> {
        const allReports = await this.getAllReports('weekly') as WeeklyReport[];
        return allReports.filter((report) => report.projectGroupId === projectGroupId);
    }

    public async findAllDailyWageReportsByProjectGroup(projectGroupId: number): Promise<DailyWageReport[]> {
        const allReports = await this.getAllReports('daily-wage') as DailyWageReport[];
        return allReports.filter((report) => report.projectGroupId === projectGroupId);
    }

    public async findAllDailyWageReportsByRepair(repairId: number): Promise<DailyWageReport[]> {
        const allReports = await this.getAllReports('daily-wage') as DailyWageReport[];
        return allReports.filter((report) => report.repairId === repairId);
    }

    private async getAllReports(reportType: ReportType): Promise<StoredReport[]> {
        const userId = this.getUserId();
        if (!userId) return [];

        await this.ensureMigrationDone(userId);
        return this.getReports(reportType, userId);
    }

    public async deleteWeeklyReportByOfflineId(offlineId: string): Promise<void> {
        return this.deleteReportByOfflineId('weekly', offlineId);
    }

    public async deleteDailyWageReportByOfflineId(offlineId: string): Promise<void> {
        return this.deleteReportByOfflineId('daily-wage', offlineId);
    }

    private async deleteReportByOfflineId(reportType: ReportType, offlineId: string): Promise<void> {
        const userId = this.getUserId();
        if (!userId) return;

        await this.ensureMigrationDone(userId);

        let reports = await this.getReports(reportType, userId);
        reports = reports.filter((report) => report.offlineId !== offlineId);
        await this.setReports(reportType, userId, reports);
    }

    public async deleteWeeklyReport(reportId: number): Promise<void> {
        return this.deleteReport('weekly', reportId);
    }

    public async deleteDailyWageReport(reportId: number): Promise<void> {
        return this.deleteReport('daily-wage', reportId);
    }

    private async deleteReport(reportType: ReportType, reportId: number): Promise<void> {
        const userId = this.getUserId();
        if (!userId) return;

        await this.ensureMigrationDone(userId);

        let reports = await this.getReports(reportType, userId);
        reports = reports.filter((report) => report.id !== reportId);
        await this.setReports(reportType, userId, reports);
    }

    private getUserId(): string | undefined {
        const userId = localStorage.getItem('OMB_USER_ID');
        if (!userId) {
            console.warn('Cannot use IndexedDB while not logged in!');
            return undefined;
        }
        return userId;
    }

    private getDb(): Promise<IDBDatabase> {
        if (!this.dbPromise) {
            this.dbPromise = new Promise((resolve, reject) => {
                if (!window.indexedDB) {
                    reject(new Error('IndexedDB is not available in this browser'));
                    return;
                }

                const request = indexedDB.open(this.DB_NAME, this.DB_VERSION);

                request.onupgradeneeded = () => {
                    const db = request.result;
                    if (!db.objectStoreNames.contains(this.STORE_NAME)) {
                        db.createObjectStore(this.STORE_NAME, { keyPath: 'key' });
                    }
                };

                request.onsuccess = () => resolve(request.result);
                request.onerror = () => reject(request.error || new Error('Failed to open IndexedDB'));
            });
        }

        return this.dbPromise;
    }

    private async getReports(reportType: ReportType, userId: string): Promise<StoredReport[]> {
        const db = await this.getDb();
        const key = this.getStorageKey(reportType, userId);
        const entry = await this.readEntry(db, key);
        return entry?.reports || [];
    }

    private async setReports(reportType: ReportType, userId: string, reports: StoredReport[]): Promise<void> {
        const db = await this.getDb();
        const key = this.getStorageKey(reportType, userId);
        await this.writeEntry(db, {
            key,
            reports,
        });
    }

    private async ensureMigrationDone(userId: string): Promise<void> {
        const marker = `${this.MIGRATION_MARKER_PREFIX}.${userId}`;
        if (localStorage.getItem(marker) === 'true') {
            return;
        }

        const reportTypes: ReportType[] = ['weekly', 'daily-wage'];

        await Promise.all(reportTypes.map(async (reportType) => {
            const legacyKey = this.getStorageKey(reportType, userId);
            const legacyValue = localStorage.getItem(legacyKey);
            if (!legacyValue) return;

            let legacyReports: StoredReport[] = [];
            try {
                legacyReports = JSON.parse(legacyValue) as StoredReport[];
            } catch (e) {
                console.error(`Could not parse migrated report data for key ${legacyKey}`, e);
                return;
            }

            if (!legacyReports.length) return;

            const existingReports = await this.getReports(reportType, userId);
            const mergedReports = this.mergeReports(existingReports, legacyReports);
            await this.setReports(reportType, userId, mergedReports);
            localStorage.removeItem(legacyKey);
        }));

        localStorage.setItem(marker, 'true');
    }

    private mergeReports(existingReports: StoredReport[], incomingReports: StoredReport[]): StoredReport[] {
        const merged = clone(existingReports);

        for (let i = 0; i < incomingReports.length; i += 1) {
            const report = clone(incomingReports[i]);

            if (!report.offlineId) {
                report.offlineId = uuidv4();
            }

            const idx = merged.findIndex((r) => this.isSameReport(r, report));
            if (idx >= 0) {
                merged[idx] = report;
            } else {
                merged.push(report);
            }
        }

        return merged;
    }

    private isSameReport(left: StoredReport, right: StoredReport): boolean {
        return (left.id !== undefined && right.id !== undefined && left.id === right.id)
            || (left.offlineId !== undefined && right.offlineId !== undefined && left.offlineId === right.offlineId);
    }

    private getStorageKey(type: ReportType, userId: string): string {
        const prefix = type === 'weekly' ? 'OMB_WEEKLY_REPORTS' : 'OMB_DAILY_WAGE_REPORTS';
        return `${prefix}.${userId}`;
    }

    private readEntry(db: IDBDatabase, key: string): Promise<ReportsStoreEntry | undefined> {
        return new Promise((resolve, reject) => {
            const transaction = db.transaction(this.STORE_NAME, 'readonly');
            const store = transaction.objectStore(this.STORE_NAME);
            const request = store.get(key);

            request.onsuccess = () => resolve(request.result as ReportsStoreEntry | undefined);
            request.onerror = () => reject(request.error || new Error('Failed to read from IndexedDB'));
        });
    }

    private writeEntry(db: IDBDatabase, entry: ReportsStoreEntry): Promise<void> {
        return new Promise((resolve, reject) => {
            const transaction = db.transaction(this.STORE_NAME, 'readwrite');
            const store = transaction.objectStore(this.STORE_NAME);
            store.put(entry);

            transaction.oncomplete = () => resolve();
            transaction.onerror = () => reject(transaction.error || new Error('Failed to write to IndexedDB'));
            transaction.onabort = () => reject(transaction.error || new Error('IndexedDB transaction aborted'));
        });
    }
}
